import styled from 'styled-components';

export const CardGrid = styled.div.withConfig({
  shouldForwardProp: (prop) => !['columns', 'minWidth'].includes(prop),
})`
  display: grid;
  grid-template-columns: ${({ columns, minWidth }) =>
    columns
      ? `repeat(${columns}, minmax(0, 1fr))`
      : `repeat(auto-fill, minmax(${minWidth || '210px'}, 1fr))`};
  gap: 12px;
  width: 100%;

  ${({ columns }) =>
    columns
      ? `@media (max-width: 900px) {
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  }`
      : ''}
`;

export const Card = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  border-radius: 10px;
  border: 1px solid
    ${({ $selected, theme }) => ($selected ? theme.colors.primary : theme.colors.lightGray)};
  background-color: ${({ theme }) => theme.colors.backgroundSecondary};
  padding: 12px;
  min-height: 110px;
  cursor: ${({ $clickable }) => ($clickable === false ? 'default' : 'pointer')};
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  ${({ $selected, theme }) =>
    $selected ? `box-shadow: 0 0 0 1px ${theme.colors.primary};` : ''}

  &:hover {
    border-color: ${({ $clickable, theme }) =>
      $clickable === false ? theme.colors.lightGray : theme.colors.primary};
  }
`;

export const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
`;

export const CardTitle = styled.span`
  font-weight: 700;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.text};
`;

export const CardSubtitle = styled.span`
  display: block;
  font-size: 0.72rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

export const CornerTag = styled.span`
  position: absolute;
  top: -9px;
  left: 12px;
  background-color: ${({ theme }) => theme.colors.primary};
  color: #fff;
  font-size: 0.62rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  line-height: 1.4;
`;

export const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  min-width: 66px;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
  color: #fff;
  white-space: nowrap;
  text-align: center;
  cursor: ${({ onClick }) => (onClick ? 'pointer' : 'default')};
  transition: filter 0.15s ease;
  background-color: ${({ $active, theme }) =>
    $active ? theme.colors.success : theme.colors.lightGray};

  ${({ onClick }) =>
    onClick
      ? `&:hover {
    filter: brightness(0.92);
  }`
      : ''}
`;

export const ItemList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 8px 0;
`;

export const ItemRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  border-radius: 6px;
  padding: 6px 8px;
  background-color: ${({ theme }) => theme.colors.background};
`;

export const ItemText = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  color: ${({ $muted, theme }) => ($muted ? theme.colors.textSecondary : theme.colors.text)};
  text-decoration: ${({ $strike }) => ($strike ? 'line-through' : 'none')};
`;

export const MenuButton = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 2px 4px;
  color: ${({ theme }) => theme.colors.textSecondary};
  display: flex;
  align-items: center;
  flex-shrink: 0;

  &:hover {
    color: ${({ theme }) => theme.colors.text};
  }
`;

export const MenuOption = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  border: none;
  background: transparent;
  padding: 6px 10px;
  font-size: 0.82rem;
  cursor: pointer;
  color: ${({ $danger, theme }) => ($danger ? theme.colors.danger : theme.colors.text)};
  border-radius: 4px;

  &:hover {
    background-color: ${({ theme }) => theme.colors.lightGrayOpacity};
  }
`;

export const AddButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: auto;
  border: 1px dashed ${({ theme }) => theme.colors.lightGray};
  border-radius: 6px;
  background: transparent;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 0.8rem;
  font-weight: 600;
  padding: 8px;
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.colors.lightGrayOpacity};
  }
`;

export const Hint = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 14px;
  padding: 10px 14px;
  border-radius: 8px;
  background-color: ${({ theme }) => theme.colors.backgroundSecondary};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.82rem;
`;

export const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.85rem;
`;

export const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  color: ${({ theme }) => theme.colors.textSecondary};
`;
